import AppLayout from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StrandBadge from "@/components/StrandBadge";
import PhysicalStrandResults from "@/components/PhysicalStrandResults";
import ModeBadge from "@/components/ModeBadge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Star,
  Target,
  Dumbbell,
  Clock,
  ArrowLeft,
  CheckCircle,
  Play,
  AlertTriangle,
  Sparkles,
  MessageCircle,
  MessageSquare,
  Loader2,
  FileAudio,
  Info,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useRef, useState, type ReactNode, type RefObject } from "react";
import { useAssessment, useAssessmentDraftReport, useAssessmentSignedReport, useSignOffAssessment, useRecordingUrl, useAssessmentPhysicalResult, useAssessmentTranscript, useSaveReflections, type Assessment, type DraftReport, type SignedReport } from "@/lib/api/assessments";
import { useAssessmentStatus } from "@/lib/api/websocket";
import { formatTime, ensureFiniteDuration } from "@/lib/audio";

type StrandKey = "physical" | "linguistic" | "cognitive" | "social_emotional";

interface StrandScoreData {
  band: string;
  score?: number;  // 1-5 scale for physical strand
  confidence: number;
  evidence_clips?: string[];
  justification?: string;
  subskills?: Record<string, { score?: number; band?: string }>;
}

const BAND_TO_SCORE: Record<string, number> = {
  emerging: 1,
  expected: 2,
  exceeding: 3,
};

const STRAND_LABELS: Record<StrandKey, { label: string; childLabel: string; emoji: string }> = {
  physical: { label: "Physical", childLabel: "How you sound", emoji: "🗣️" },
  linguistic: { label: "Linguistic", childLabel: "Your words", emoji: "📝" },
  cognitive: { label: "Cognitive", childLabel: "Your ideas", emoji: "🧠" },
  social_emotional: { label: "Social & Emotional", childLabel: "Your audience", emoji: "💬" },
};

const STRAND_KEYS: StrandKey[] = ["physical", "linguistic", "cognitive", "social_emotional"];

const AVAILABLE_STRANDS: StrandKey[] = ["physical"];

const scoreLabels: Record<number, { text: string; color: string }> = {
  1: { text: "Getting started", color: "bg-destructive/10 text-destructive" },
  2: { text: "Building up", color: "bg-warning/10 text-warning" },
  3: { text: "Doing well", color: "bg-primary/10 text-primary" },
  4: { text: "Great!", color: "bg-success/10 text-success" },
  5: { text: "Brilliant!", color: "bg-success/10 text-success" },
};

// Audio playback for the submitted recording, matching the demo results screen
// (a plain audio control with play/scrubber/volume/duration). Shared so both
// the self-serve and signed-off views render the same control at the top.
function RecordingPlayer({
  recordingUrl,
  recordingLoading,
  audioRef,
}: {
  recordingUrl?: string;
  recordingLoading?: boolean;
  audioRef: RefObject<HTMLAudioElement>;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        {recordingLoading ? (
          <p className="text-sm text-muted-foreground">Loading recording…</p>
        ) : recordingUrl ? (
          <audio
            ref={audioRef}
            src={recordingUrl}
            controls
            preload="auto"
            className="w-full"
            onLoadedMetadata={(e) => ensureFiniteDuration(e.currentTarget)}
          />
        ) : (
          <p className="text-sm text-muted-foreground">No recording available</p>
        )}
      </CardContent>
    </Card>
  );
}

// Transcript section, matching the demo results screen: same heading, same
// scrollable panel, clickable words that seek the audio above. Sourced from the
// ASR transcript for the submitted recording.
function RecordingTranscript({
  assessmentId,
  audioRef,
}: {
  assessmentId: string;
  audioRef: RefObject<HTMLAudioElement>;
}) {
  const { data: transcript, isLoading } = useAssessmentTranscript(assessmentId);

  const words = (transcript?.segments || []).flatMap((seg) => seg.words || []);
  const fullText = transcript?.full_text?.trim() || "";

  const seek = (start: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = start;
      void audioRef.current.play();
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary" />
          Transcript
        </h2>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        ) : words.length > 0 ? (
          <div className="text-sm text-foreground leading-relaxed bg-secondary/30 rounded-lg p-4 max-h-48 overflow-y-auto">
            {words.map((w, i) => (
              <button
                key={i}
                onClick={() => seek(w.start)}
                className="hover:bg-primary/10 rounded px-0.5 transition-colors"
                title={`${formatTime(w.start)} (click to seek)`}
              >
                {w.word}{" "}
              </button>
            ))}
          </div>
        ) : fullText ? (
          <div className="text-sm text-foreground leading-relaxed bg-secondary/30 rounded-lg p-4 max-h-48 overflow-y-auto">
            {fullText}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground p-4 bg-secondary/30 rounded-lg">
            Transcript unavailable for this recording.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function getStrandScore(report: DraftReport | SignedReport, strand: StrandKey): { score: number; band: string; confidence: number; justification: string } {
  const scoreKey = `${strand}_score` as keyof typeof report;
  const strandData = report[scoreKey] as StrandScoreData;
  
  // Physical now has direct 1-5 score from deterministic scoring
  if (strand === "physical" && strandData) {
    const physData = strandData as { score?: number; band?: string };
    const directScore = physData.score;
    if (directScore !== undefined) {
      // Map 1-5 to band for backward compatibility
      let band = physData.band || "emerging";
      if (directScore < 2) band = "emerging";
      else if (directScore < 4) band = "expected";
      else band = "exceeding";
      
      return {
        score: directScore,  // Use the 1-5 score directly
        band,
        confidence: strandData?.confidence || 0,
        justification: strandData?.justification || "",
      };
    }
  }
  
  const band = strandData?.band || "emerging";
  return {
    score: BAND_TO_SCORE[band] || 1,
    band,
    confidence: strandData?.confidence || 0,
    justification: strandData?.justification || "",
  };
}

function FeedbackContent({ 
  assessment, 
  report,
  recordingUrl,
  recordingLoading,
}: { 
  assessment: Assessment; 
  report: DraftReport | SignedReport;
  recordingUrl?: string;
  recordingLoading?: boolean;
}) {
  const [reflections, setReflections] = useState<string[]>(["", ""]);
  const [reflectionsSaved, setReflectionsSaved] = useState(false);
  const [drillCompleted, setDrillCompleted] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Deterministic Physical Strand result (spec-driven, no LLM). When present it
  // is the source of truth for the results, using the shared template.
  const { data: physical } = useAssessmentPhysicalResult(assessment.id);
  const saveReflections = useSaveReflections();

  const handleSaveReflections = () => {
    saveReflections.mutate(
      { assessmentId: assessment.id, notes: reflections },
      { onSuccess: () => setReflectionsSaved(true) },
    );
  };

  const handlePlayMoment = (startMs: number | null) => {
    if (startMs === null) return;
    console.log(`Seeking to ${startMs}ms`);
  };

  // Feedback items are strings, but the pipeline has historically sent objects
  // like { text, strand, evidence }. Pull the text out either way so an object
  // can never be rendered as a React child (that throws and blanks the page).
  const feedbackText = (item: unknown): string =>
    typeof item === "string" ? item : ((item as { text?: string })?.text ?? "");

  const feedback = report.feedback as {
    strengths?: unknown[];
    next_steps?: unknown[];
    goals?: string[];
  };
  const strengths = feedback?.strengths || [];
  const nextSteps = feedback?.next_steps || [];
  const warnings = report.warnings as string[] || [];
  const overallConfidence = report.overall_confidence || 0;
  const lowConfidence = overallConfidence < 0.6;

  const createdDate = new Date(assessment.created_at);
  const submittedAgo = createdDate.toLocaleDateString();

  return (
    <>
      {/* Low-confidence warning */}
      {lowConfidence && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Card className="border-warning/30 bg-warning/5">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Some feedback may be limited</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  The recording was a bit hard to hear in places, so your teacher has checked the scores carefully. If anything looks wrong, ask your teacher!
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="p-4">
            <p className="text-xs text-warning">
              {warnings.join(", ")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Audio player */}
      <RecordingPlayer recordingUrl={recordingUrl} recordingLoading={recordingLoading} audioRef={audioRef} />

      {/* Task instructions — what the student was asked to do */}
      {assessment.prompt?.instructions && (
        <Card className="bg-secondary/50 border-none">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-primary" />
              <p className="text-sm font-medium text-foreground">Task Instructions</p>
            </div>
            <div className="space-y-2 text-md text-pretty text-muted-foreground">
              {assessment.prompt.instructions}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deterministic Physical Strand results (shared template) */}
      {physical && <PhysicalStrandResults physical={physical} />}

      {/* Strand score cards */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-1">How did you do?</h2>
        <p className="text-sm text-muted-foreground mb-4">Here are your scores across four areas of oracy.</p>
        <div className="grid grid-cols-2 gap-3">
          {STRAND_KEYS.map((strand, i) => {
            const isAvailable = AVAILABLE_STRANDS.includes(strand);
            const { score } = isAvailable ? getStrandScore(report, strand) : { score: 0 };
            const maxScore = strand === "physical" ? 5 : 3;
            const level = scoreLabels[Math.min(score, 5)];
            const strandLabel = STRAND_LABELS[strand];
            return (
              <motion.div
                key={strand}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
              >
                <Card className="overflow-hidden">
                  <CardContent className="p-4 text-center">
                    <span className="text-2xl mb-1 block">{strandLabel.emoji}</span>
                    <p className="text-sm font-medium text-foreground mb-1">{strandLabel.childLabel}</p>
                    {isAvailable ? (
                      <>
                        <div className="flex items-center justify-center gap-1.5 mb-2">
                          {strand === "physical" ? (
                            [...Array(5)].map((_, n) => (
                              <div
                                key={n}
                                className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                                  n < score
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted text-muted-foreground"
                                }`}
                              >
                                {n + 1}
                              </div>
                            ))
                          ) : (
                            [...Array(3)].map((_, n) => (
                              <div
                                key={n}
                                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                                  n < score
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted text-muted-foreground"
                                }`}
                              >
                                {n + 1}
                              </div>
                            ))
                          )}
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${level.color}`}>
                          {level.text}
                        </span>
                      </>
                    ) : (
                      <div className="py-4 text-sm text-muted-foreground">Coming Soon</div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Strengths (LLM feedback — shown only when the deterministic Physical
          Strand result is unavailable; otherwise the shared template above
          carries reproducible, descriptor-based strengths and next steps) */}
      {!physical && (
      <>
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-5 h-5 text-success" />
            <h2 className="font-semibold text-foreground">{Math.min(strengths.length, 3)} things you did well</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Tap the play button to hear the exact moment from your talk.
          </p>
          {strengths.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No strengths recorded yet. Ask your teacher to add feedback!</p>
          ) : (
            <div className="space-y-3">
              {strengths.slice(0, 3).map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.1 }}
                  className="p-3 rounded-lg bg-success/5 border border-success/10"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-success mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">{feedbackText(s)}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Next steps */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-5 h-5 text-warning" />
            <h2 className="font-semibold text-foreground">{Math.min(nextSteps.length, 3)} things to try next time</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            These ideas will help you improve.
          </p>
          {nextSteps.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No next steps recorded yet. Ask your teacher to add feedback!</p>
          ) : (
            <div className="space-y-3">
              {nextSteps.slice(0, 3).map((ns, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.1 }}
                  className="p-3 rounded-lg bg-warning/5 border border-warning/10"
                >
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-warning/20 flex items-center justify-center text-xs font-bold text-warning shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">{feedbackText(ns)}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {/* Transcript — below Strengths/Next Steps, matching the demo screen */}
      <RecordingTranscript assessmentId={assessment.id} audioRef={audioRef} />

      {/* Reflection prompt */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <MessageCircle className="w-5 h-5 text-primary" />
            <h2 className="font-semibold text-foreground">Think about your talk</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Write a few sentences for each question. Your answers are saved in your portfolio.
          </p>
          <div className="space-y-4">
            {[
              "What part of your talk are you most proud of?",
              "If you could redo one part, what would you change and why?",
            ].map((q, i) => (
              <div key={i}>
                <label className="text-sm font-medium text-foreground block mb-1.5">
                  {i + 1}. {q}
                </label>
                <Textarea
                  placeholder="Write your thoughts here…"
                  value={reflections[i]}
                  onChange={(e) => {
                    const updated = [...reflections];
                    updated[i] = e.target.value;
                    setReflections(updated);
                    if (reflectionsSaved) setReflectionsSaved(false);
                  }}
                  rows={3}
                  className="text-sm"
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Button
              size="sm"
              onClick={handleSaveReflections}
              disabled={reflections.every((r) => r.trim() === "") || reflectionsSaved}
              className="gap-1.5"
            >
              {reflectionsSaved ? (
                <>
                  <CheckCircle className="w-4 h-4" /> Saved
                </>
              ) : (
                "Save my reflections"
              )}
            </Button>
            {reflectionsSaved && (
              <span className="text-xs text-success">Added to your portfolio ✓</span>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-12 w-64" />
      <Skeleton className="h-24 w-full" />
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function ProcessingState({ status }: { status: string }) {
  return (
    <Card>
      <CardContent className="p-8 text-center">
        <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {status === "processing" ? "Processing your recording..." : "Getting ready..."}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {status === "processing" 
            ? "We're analyzing your speech and generating feedback. This usually takes a minute or two."
            : "Your assessment is being prepared."}
        </p>

      </CardContent>
    </Card>
  );
}

function PendingState() {
  return (
    <Card>
      <CardContent className="p-8 text-center">
        <FileAudio className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold text-foreground mb-2">No recording yet</h2>
        <p className="text-sm text-muted-foreground mb-4">
          This assessment doesn't have a recording uploaded yet.
        </p>
        <Link to="/student/tasks">
          <Button>Go to Tasks</Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function DraftReadyState() {
  return (
    <Card>
      <CardContent className="p-8 text-center">
        <Loader2 className="w-12 h-12 mx-auto text-warning mb-4" />
        <h2 className="text-xl font-semibold text-foreground mb-2">Waiting for teacher review</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Your feedback is ready! Your teacher needs to review and approve it before you can see the full results.
        </p>
        <p className="text-xs text-muted-foreground">
          Ask your teacher to review this assessment to unlock your feedback.
        </p>
      </CardContent>
    </Card>
  );
}

// Shown at draft_ready when the score-visibility flag is on for this phase: the
// student sees their deterministic Physical Strand result straight away, while
// the full LLM report (other strands + feedback) still unlocks after the teacher
// signs off. The physical score is deterministic so it does not change on review.
function PhysicalOnlyState({ assessmentId, fallback }: { assessmentId: string; fallback?: ReactNode }) {
  const { data: physical, isLoading } = useAssessmentPhysicalResult(assessmentId);
  const { data: recordingUrl, isLoading: recordingLoading } = useRecordingUrl(assessmentId);
  const audioRef = useRef<HTMLAudioElement>(null);

  if (isLoading) return <LoadingState />;
  if (!physical) return <>{fallback ?? <DraftReadyState />}</>;

  return (
    <div className="space-y-6">
      <RecordingPlayer recordingUrl={recordingUrl} recordingLoading={recordingLoading} audioRef={audioRef} />
      <PhysicalStrandResults physical={physical} />
      <Card className="border-warning/30 bg-warning/5">
        <CardContent className="p-4 text-center">
          <p className="text-sm text-muted-foreground">
            Your full feedback across the other strands unlocks once your teacher reviews this assessment.
          </p>
        </CardContent>
      </Card>
      <RecordingTranscript assessmentId={assessmentId} audioRef={audioRef} />
    </div>
  );
}

function ErrorState({ error }: { error: string }) {
  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
        <h2 className="text-xl font-semibold text-foreground mb-2">Something went wrong</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {error || "There was an error processing your assessment."}
        </p>
        <Link to="/student">
          <Button variant="outline">Back to Dashboard</Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export default function FeedbackPage() {
  const { id } = useParams<{ id: string }>();
  
  const { data: assessment, isLoading: assessmentLoading, error: assessmentError } = useAssessment(id!);
  const { data: signedReport, isLoading: signedLoading } = useAssessmentSignedReport(id!);
  const { data: draftReport, isLoading: draftLoading } = useAssessmentDraftReport(id!);
  const { data: recordingUrl, isLoading: recordingLoading } = useRecordingUrl(id!);

  useAssessmentStatus(id || null);

  if (assessmentLoading) {
    return (
      <AppLayout role="student">
        <div className="max-w-2xl mx-auto space-y-6 pb-12">
          <Skeleton className="h-8 w-48" />
          <LoadingState />
        </div>
      </AppLayout>
    );
  }

  if (assessmentError || !assessment) {
    return (
      <AppLayout role="student">
        <div className="max-w-2xl mx-auto space-y-6 pb-12">
          <Link
            to="/student"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <ErrorState error={assessmentError?.message || "Failed to load assessment"} />
        </div>
      </AppLayout>
    );
  }

  const status = assessment.status;

  return (
    <AppLayout role="student">
      <div className="max-w-2xl mx-auto space-y-6 pb-12">
        <Link
          to="/student"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <ModeBadge mode={assessment.mode} />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {assessment.prompt_name || "Assessment"} — Your Feedback
          </h1>
          <p className="text-sm text-muted-foreground">
            Submitted {new Date(assessment.uploaded_at || assessment.created_at).toLocaleDateString()}
          </p>
        </motion.div>

        {status === "pending" || status === "uploading" ? (
          assessment.has_recording ? (
            <ProcessingState status={status} />
          ) : (
            <PendingState />
          )
        ) : status === "processing" || status === "draft_ready" || status === "under_review" ? (
          status === "processing" ? (
            <ProcessingState status={status} />
          ) : signedLoading || draftLoading ? (
            <LoadingState />
          ) : assessment.physical_score_visible_to_student ? (
            <PhysicalOnlyState assessmentId={assessment.id} />
          ) : (
            <DraftReadyState />
          )
        ) : status === "signed_off" ? (
          signedReport ? (
            <FeedbackContent assessment={assessment} report={signedReport} recordingUrl={recordingUrl} recordingLoading={recordingLoading} />
          ) : draftReport ? (
            <FeedbackContent assessment={assessment} report={draftReport} recordingUrl={recordingUrl} recordingLoading={recordingLoading} />
          ) : (
            <LoadingState />
          )
        ) : status === "error" ? (
          // An LLM-report failure can mark the whole assessment "error" even
          // though the deterministic Physical Strand score landed fine. When the
          // student is allowed to see the physical result, show it instead of the
          // dead-end "contact your teacher" screen (the score doesn't use the LLM).
          assessment.physical_score_visible_to_student ? (
            <PhysicalOnlyState
              assessmentId={assessment.id}
              fallback={<ErrorState error="There was an error processing your assessment. Please contact your teacher." />}
            />
          ) : (
            <ErrorState error="There was an error processing your assessment. Please contact your teacher." />
          )
        ) : (
          <ProcessingState status={status} />
        )}
      </div>
    </AppLayout>
  );
}
